import dayjs from "dayjs";
import { HttpStatusCode } from "axios";
import { axiosEjFetch as axiosFetch } from "./axiosEjHelpers";

export async function getVideoList(reservationId: string) {
  return axiosFetch.get(`/api/Media/getVideoList/${reservationId}`);
}

export async function getPhotoList(
  reservationId: string,
  includeDetails = false
) {
  return axiosFetch.get(
    `/api/Media/getPhotoList/${reservationId}?includeDetails=${includeDetails}`
  );
}

export async function getPhotoListForSession(
  sessionId: string,
  includeDetails = false
) {
  return axiosFetch.get(
    `/api/Media/getPhotoListForSession/${sessionId}?includeDetails=${includeDetails}`
  );
}

export async function getVideoListForSession(sessionId: string) {
  return axiosFetch.get(`/api/Media/getVideoListForSession/${sessionId}`);
}

function utcNow() {
  return dayjs(Date.now()).utc().toISOString();
}

export async function setPhotoSeen(photoId: string) {
  const body = [
    {
      op: "replace",
      path: `/seenOnUtc`,
      value: utcNow(),
    },
  ];
  return axiosFetch.patch(`/api/media/${photoId}`, body);
}

export async function setVideoSeen(videoId: string) {
  const body = [
    {
      op: "replace",
      path: `/seenOnUtc`,
      value: utcNow(),
    },
  ];
  return axiosFetch.patch(`/api/media/${videoId}`, body);
}

export async function setMediaDate(
  mediaId: string,
  newDate: string | undefined
) {
  const body = [
    {
      op: "replace",
      path: `/RecordedOnUtc`,
      value: newDate,
    },
  ];
  return axiosFetch.patch(`/api/media/${mediaId}`, body);
}

export async function downloadMedia(
  reservationId: string,
  photoIds: string[],
  videoIds: string[]
): Promise<string | null> {
  const res = await axiosFetch.safePost<any, any, any, string>(
    "/api/media/downloadMedia",
    {
      reservationId,
      photoIds,
      videoIds,
    }
  );
  // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
  return res.status == HttpStatusCode.Ok ? res.data : null;
}

export async function downloadMediaForSession(
  sessionId: string,
  photoIds: string[],
  videoIds: string[]
): Promise<string | null> {
  const res = await axiosFetch.safePost<any, any, any, string>(
    "/api/media/downloadMediaForSession",
    {
      sessionId,
      photoIds,
      videoIds,
    }
  );
  // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
  return res.status == HttpStatusCode.Ok ? res.data : null;
}

export async function downloadZip(zipId: string) {
  return axiosFetch.post(
    "/api/media/downloadZip",
    { zipId },
    {}, // headers
    { responseType: "blob" }
  );
}

export type ZipInfo = {
  zipId: string;
  progress: number;
};

export async function getZipInfo(
  zipId: string,
  accessToken?: string
): Promise<ZipInfo | null> {
  try {
    // Guests on the QR gallery authenticate with their session token, the same
    // way deleteMedia and printMedia do.
    const headers = accessToken
      ? { Authorization: `Bearer ${accessToken}` }
      : undefined;
    return await axiosFetch.safeGet<any, any, ZipInfo>(
      `/api/media/zipInfo/${zipId}`,
      undefined,
      headers
    );
  } catch {
    return null;
  }
}

export async function deleteMedia(
  photoIds: string[],
  videoIds: string[],
  accessToken?: string
) {
  if (accessToken) {
    return axiosFetch.post(
      "/api/media/deleteMedia",
      { photoIds, videoIds },
      {
        Authorization: `Bearer ${accessToken}`,
      }
    );
  }

  return axiosFetch.post("/api/media/deleteMedia", {
    photoIds,
    videoIds,
  });
}

export type PrintMediaResult = {
  success: boolean;
  message: string;
  count: number;
};

export async function printMedia(
  photoIds: string[],
  accessToken?: string
): Promise<PrintMediaResult | null> {
  const headers = accessToken
    ? { Authorization: `Bearer ${accessToken}` }
    : undefined;
  const res = await axiosFetch.safePost<any, any, any, PrintMediaResult>(
    "/api/media/printMedia",
    { photoIds },
    headers
  );
  // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
  return res.status == HttpStatusCode.Ok ? res.data : null;
}
